import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { showQuickAddCreatedToast } from '../utils/quickAddCreatedToast'

/** So lange wartet das Hauptfenster nach dem Anlegen darauf, dass die Tastatur zu ihm zurückkommt. */
export const QUICK_ADD_FOCUS_WAIT_MS = 1000

/**
 * **Kommt die Tastatur zurück?** Geht die Schnelleingabe zu, bekommt die vorher aktive App den Fokus
 * zurück — das Hauptfenster also nur, wenn HappyTask vorn war. Der Main-Prozess müsste das beim Öffnen
 * raten; das Fenster merkt es selbst.
 */
function focusReturns(): Promise<boolean> {
  if (document.hasFocus()) return Promise.resolve(true)
  return new Promise((resolve) => {
    const done = (back: boolean) => {
      clearTimeout(timer)
      window.removeEventListener('focus', onFocus)
      resolve(back)
    }
    const onFocus = () => done(true)
    const timer = setTimeout(() => done(false), QUICK_ADD_FOCUS_WAIT_MS)
    window.addEventListener('focus', onFocus)
  })
}

/**
 * **Das Hauptfenster sagt, wo die Aufgabe aus der Schnelleingabe gelandet ist** (Sven, 04.10.2026):
 * „„Blumen gießen“ angelegt“, darunter Ort und Angaben, mit „Anzeigen“, das hinspringt
 * (`showQuickAddCreatedToast`). Zu sehen, sobald HappyTask vorn ist. War HappyTask vorn, hat „Anzeigen“
 * den Fokus — Enter springt hin (`.claude/tasks/quick-add-reveal.md`). Nach ⇧↵ (`reveal`) kein Toast,
 * da springt das Fenster selbst hin (`useQuickAddReveal`).
 *
 * Die Schnelleingabe ist ein eigenes Fenster und meldet `quick-add:created` mit Kennung und Zeile; die
 * Aufgabe holt das Hauptfenster selbst aus derselben Datenbank. Im Web fehlt der Kanal — dort zeigt der
 * Dialog den Toast selbst (`QuickAddDialog`).
 */
export function useQuickAddCreatedToast(): void {
  const { t } = useTranslation()
  const source = useDataSource()

  useEffect(() => window.windowAPI?.onWindowMessage?.('quick-add:created', (data) => {
    const { taskId, details, reveal } = (data ?? {}) as { taskId?: unknown; details?: unknown; reveal?: unknown }
    if (typeof taskId !== 'string' || typeof details !== 'string' || reveal === true) return
    // Sofort horchen, nicht erst nach dem Laden der Aufgabe — sonst ginge der Fokus-Wechsel verloren.
    const focused = focusReturns()
    void Promise.all([source.getTask(taskId), focused]).then(([task, focusAction]) => {
      if (task) showQuickAddCreatedToast(t, task, details, { focusAction })
    })
  }), [source, t])
}
